import React from "react";
import {
  ASSET_STATUS,
  BRAND_ASSET_CATEGORY_COUNT,
  BRAND_ASSET_LABELS,
  GROUP_OVERRIDE_SETTINGS,
  SHARED_RECORD_TYPES,
  STATUS_PILL,
  formatNetworkDateTime,
  getAllowedOverrides,
  getControlLevels,
  CONTROL_LEVELS,
  getSharedRecordTypes,
  isDarkColor,
  resolveSmsProvider,
  safeColor,
} from "utils/network";

const Pill = ({ children, className }) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${className}`}
  >
    {children}
  </span>
);

const Block = ({ title, hint, children }) => (
  <section className="min-w-0">
    <div className="flex items-baseline justify-between gap-3 mb-2.5">
      <h5 className="text-[11px] uppercase tracking-[0.06em] font-semibold text-black">
        {title}
      </h5>
      {hint ? <span className="text-xs text-darkgray">{hint}</span> : null}
    </div>
    {children}
  </section>
);

const Panel = ({ children }) => (
  <div className="border border-mediumgray rounded-md overflow-hidden bg-white">
    {children}
  </div>
);

// Rows share one top border so the panel reads as a single object rather than a
// stack of separate cards.
const rowBorder = "border-t border-mediumgray first:border-t-0";

const DlRow = ({ label, value, href }) => (
  <div className={`flex items-baseline gap-4 px-4 py-2.5 ${rowBorder}`}>
    <span className="text-[13px] text-darkgray flex-shrink-0">{label}</span>
    <span className="ml-auto min-w-0 text-right break-words">
      {value ? (
        href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-black border-b border-mediumgray hover:border-black"
          >
            {value}
          </a>
        ) : (
          <span className="text-black">{value}</span>
        )
      ) : (
        <span className="text-darkgray">Not set</span>
      )}
    </span>
  </div>
);

const StateRow = ({ name, description, children }) => (
  <div className={`flex items-start gap-3.5 px-4 py-3 ${rowBorder}`}>
    <div className="min-w-0">
      <div className="font-medium text-black">{name}</div>
      {description ? (
        <div className="text-xs text-darkgray mt-0.5">{description}</div>
      ) : null}
    </div>
    <div className="ml-auto flex-shrink-0 pt-px">{children}</div>
  </div>
);

const Stat = ({ label, value, suffix, small }) => (
  <div className="px-4 py-3.5 border-l border-mediumgray first:border-l-0">
    <div className="text-[11px] uppercase tracking-[0.06em] font-medium text-darkgray">
      {label}
    </div>
    <div
      className={`mt-1 font-semibold text-black tabular-nums leading-tight ${
        small ? "text-sm font-medium" : "text-xl"
      }`}
    >
      {value}
      {suffix ? (
        <span className="text-[13px] font-normal text-darkgray"> {suffix}</span>
      ) : null}
    </div>
  </div>
);

const Swatch = ({ label, value }) => (
  <div className="flex items-center gap-2">
    <div
      className="w-[22px] h-[22px] rounded flex-shrink-0 border border-black/10"
      style={{ backgroundColor: value }}
    />
    <div className="leading-tight">
      <div className="text-[11px] text-darkgray">{label}</div>
      <div className="text-xs font-medium text-black tabular-nums">{value}</div>
    </div>
  </div>
);

const LogoOrPlaceholder = ({ url, alt, onDark }) =>
  url ? (
    <img
      src={url}
      alt={alt}
      className="h-7 w-auto max-w-[140px] object-contain flex-shrink-0"
    />
  ) : (
    <div
      className={`flex h-7 w-7 items-center justify-center rounded text-[10px] font-semibold flex-shrink-0 ${
        onDark ? "" : "bg-lightgray text-darkgray"
      }`}
      style={onDark ? { backgroundColor: "rgba(255,255,255,0.16)" } : undefined}
    >
      Logo
    </div>
  );

export default function NetworkView({ network }) {
  if (!network) return null;

  const branding = network.branding || {};
  const controlLevels = getControlLevels(network);
  const emails = network.operationalEmails || {};
  const smsIntegration = network.integrations?.sms || {};
  const brandAssets = network.brandAssets || [];
  const organizations = network.organizations;

  const primaryColor = safeColor(branding.colors?.primary, "#000000");
  const secondaryColor = safeColor(branding.colors?.secondary, "#FFFFFF");
  const accentColor = safeColor(branding.colors?.accent, "#E5E7EB");
  const onPrimaryText = isDarkColor(primaryColor) ? "#FFFFFF" : "#000000";

  const isTwilioProvider = resolveSmsProvider(smsIntegration) === "twilio";
  const allowedCount = getAllowedOverrides(network).length;
  const sharing = network.sharing || {};
  const sharedCount = getSharedRecordTypes(sharing).length;

  const trialEndsAt = network.trialEndsAt ? new Date(network.trialEndsAt) : null;
  const trialLabel = trialEndsAt
    ? `${trialEndsAt > new Date() ? "Trial until" : "Trial ended"} ${trialEndsAt.toLocaleDateString(
        "en-AU",
        {
          day: "numeric",
          month: "short",
          year: "numeric",
          timeZone: "Australia/Sydney",
        },
      )}`
    : null;

  const complianceRules = network.compliance?.rules || [];
  const restrictedDays = network.compliance?.restrictedDays || [];
  const socialLinks = branding.socialLinks || {};
  const socialEntries = ["facebook", "instagram", "linkedin", "youtube"].filter(
    (key) => socialLinks[key],
  );

  return (
    <div className="flex flex-col gap-6">
      {/* The network rendered in its own branding — secondary logo on the brand
          colour, primary logo on white, and the palette it resolves to. */}
      <div className="border border-mediumgray rounded-md overflow-hidden">
        <div
          className="flex flex-wrap items-center gap-5 px-6 py-6"
          style={{ backgroundColor: primaryColor, color: onPrimaryText }}
        >
          <LogoOrPlaceholder
            url={branding.logos?.secondary}
            alt={`${network.name} secondary logo`}
            onDark
          />
          <div className="min-w-0 flex-1">
            <div className="text-[22px] font-bold leading-tight tracking-[-0.01em] text-balance">
              {network.name}
            </div>
            {network.slug ? (
              <div className="mt-1.5 text-[13px] opacity-75 tabular-nums">
                {network.slug}.beleef.com.au
              </div>
            ) : null}
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <span
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium capitalize border"
              style={{
                backgroundColor: "rgba(255,255,255,0.16)",
                borderColor: "rgba(255,255,255,0.24)",
              }}
            >
              {network.status || "active"}
            </span>
            {trialLabel ? (
              <span
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium border"
                style={{
                  backgroundColor: "rgba(255,255,255,0.16)",
                  borderColor: "rgba(255,255,255,0.24)",
                }}
              >
                {trialLabel}
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-5 border-t border-mediumgray bg-white px-6 py-3.5">
          <LogoOrPlaceholder
            url={branding.logos?.primary}
            alt={`${network.name} primary logo`}
          />
          <div className="ml-auto flex flex-wrap gap-3.5">
            <Swatch label="Primary" value={primaryColor} />
            <Swatch label="Secondary" value={secondaryColor} />
            <Swatch label="Accent" value={accentColor} />
            {branding.colors?.header && <Swatch label="Link Header" value={branding.colors.header} />}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 border border-mediumgray rounded-md overflow-hidden bg-white">
        <Stat
          label="Organizations"
          value={network.organizationCount ?? organizations?.length ?? 0}
        />
        <Stat
          label="Shared data"
          value={sharedCount}
          suffix={`of ${SHARED_RECORD_TYPES.length}`}
        />
        <Stat
          label="Overrides allowed"
          value={allowedCount}
          suffix={`of ${GROUP_OVERRIDE_SETTINGS.length}`}
        />
        <Stat
          label="Updated"
          value={formatNetworkDateTime(network.updatedAt)}
          small
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.55fr_1fr] gap-6 items-start">
        <div className="flex flex-col gap-6 min-w-0">
          <Block
            title="Organizations"
            hint="Businesses trading under this network"
          >
            <Panel>
              {organizations === undefined ? (
                <div className="px-4 py-3.5 text-[13px] text-darkgray">
                  Loading organizations...
                </div>
              ) : organizations.length === 0 ? (
                <div className="px-4 py-3.5 text-[13px] text-darkgray italic">
                  No organizations assigned to this network yet
                </div>
              ) : (
                organizations.map((organization) => (
                  <div
                    key={organization._id}
                    className={`flex items-center gap-3 px-4 py-3 hover:bg-lightergray ${rowBorder}`}
                  >
                    {/* These organizations trade under THIS network, so their
                        name and slug are the ones shown at the top of the page.
                        Repeating them per row would just print the same label
                        several times, so identify each by id and subscription
                        instead. */}
                    <div className="min-w-0">
                      <div className="font-medium text-black truncate font-mono text-sm">
                        {organization._id}
                      </div>
                      {organization.companySubscription ? (
                        <div className="text-xs text-darkgray">
                          {organization.companySubscription}
                        </div>
                      ) : null}
                    </div>
                    <span className="ml-auto flex-shrink-0">
                      <Pill
                        className={
                          STATUS_PILL[organization.status] || STATUS_PILL.active
                        }
                      >
                        <span className="capitalize">
                          {organization.status || "active"}
                        </span>
                      </Pill>
                    </span>
                  </div>
                ))
              )}
            </Panel>
          </Block>

          <Block
            title="Operational emails"
            hint={
              controlLevels.operationalEmails === "group"
                ? "Groups may override these"
                : controlLevels.operationalEmails === "division"
                  ? "Divisions may override these"
                  : "Network only"
            }
          >
            <Panel>
              <DlRow
                label="Settlements"
                value={emails.settlements}
                href={emails.settlements && `mailto:${emails.settlements}`}
              />
              <DlRow
                label="Concierge"
                value={emails.concierge}
                href={emails.concierge && `mailto:${emails.concierge}`}
              />
              <DlRow
                label="Key events"
                value={emails.keyEvents}
                href={emails.keyEvents && `mailto:${emails.keyEvents}`}
              />
              <DlRow
                label="Accounts"
                value={emails.accounts}
                href={emails.accounts && `mailto:${emails.accounts}`}
              />
              <DlRow
                label="Admin"
                value={emails.admin}
                href={emails.admin && `mailto:${emails.admin}`}
              />
            </Panel>
          </Block>

          <Block
            title="Brand assets"
            hint={`${brandAssets.length} of ${BRAND_ASSET_CATEGORY_COUNT} categories submitted`}
          >
            {brandAssets.length === 0 ? (
              <Panel>
                <div className="px-4 py-3.5 text-[13px] text-darkgray italic">
                  No brand assets submitted
                </div>
              </Panel>
            ) : (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(168px,1fr))] gap-2">
                {brandAssets.map((asset) => {
                  const status =
                    ASSET_STATUS[asset.status] || ASSET_STATUS.pending;
                  const fileCount = asset.files?.length || 0;

                  return (
                    <div
                      key={asset.category}
                      className="flex items-center gap-2.5 border border-mediumgray rounded-md bg-white px-3.5 py-2.5"
                    >
                      <div className="min-w-0">
                        <div className="text-[13px] font-medium text-black truncate">
                          {BRAND_ASSET_LABELS[asset.category] || asset.category}
                        </div>
                        <div className="text-[11px] text-darkgray">
                          {fileCount === 0
                            ? "No files"
                            : `${fileCount} file${fileCount === 1 ? "" : "s"}`}
                        </div>
                      </div>
                      <span className="ml-auto flex-shrink-0">
                        <Pill className={status.className}>{status.label}</Pill>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </Block>
        </div>

        <div className="flex flex-col gap-6 min-w-0">
          <Block
            title="Shared across organizations"
            hint={sharedCount === 0 ? "Nothing shared" : undefined}
          >
            <Panel>
              {SHARED_RECORD_TYPES.map((recordType) => (
                <StateRow
                  key={recordType.key}
                  name={recordType.name}
                  description={recordType.description}
                >
                  <Pill
                    className={
                      sharing[recordType.key]
                        ? "bg-green-100 text-green-800"
                        : "bg-lightgray text-darkergray"
                    }
                  >
                    {sharing[recordType.key] ? "Shared" : "Private"}
                  </Pill>
                </StateRow>
              ))}
            </Panel>
          </Block>

          <Block title="Organization overrides">
            <Panel>
              {GROUP_OVERRIDE_SETTINGS.map((setting) => (
                <StateRow
                  key={setting.key}
                  name={setting.name}
                  description={setting.description}
                >
                  <Pill
                    className={
                      controlLevels[setting.key] === "group"
                        ? "bg-blue-100 text-blue-800"
                        : controlLevels[setting.key] === "division"
                          ? "bg-amber-100 text-amber-800"
                          : "bg-lightgray text-darkergray"
                    }
                  >
                    {CONTROL_LEVELS.find(
                      (level) => level.value === controlLevels[setting.key],
                    )?.label || "Network only"}
                  </Pill>
                </StateRow>
              ))}
            </Panel>
          </Block>

          <Block title="Typography">
            <Panel>
              <DlRow label="Heading" value={branding.fonts?.heading} />
              <DlRow label="Body" value={branding.fonts?.body} />
            </Panel>
          </Block>

          <Block title="SMS">
            <Panel>
              <DlRow
                label="Provider"
                value={isTwilioProvider ? "Twilio" : "Intelli SMS"}
              />
              <DlRow label="Sender ID" value={smsIntegration.alphaSenderId} />
              {isTwilioProvider ? (
                <DlRow
                  label="Account SID"
                  value={smsIntegration.twilioAccountSid}
                />
              ) : (
                <DlRow label="Username" value={smsIntegration.username} />
              )}
            </Panel>
          </Block>

          <Block title="Compliance">
            <Panel>
              {complianceRules.length === 0 && restrictedDays.length === 0 ? (
                <div className="px-4 py-3.5 text-[13px] text-darkgray italic">
                  No compliance rules set
                </div>
              ) : (
                <>
                  {complianceRules.map((rule) => (
                    <StateRow key={rule.key || rule._id} name={rule.key}>
                      <Pill
                        className={
                          rule.enabled
                            ? "bg-green-100 text-green-800"
                            : "bg-lightgray text-darkergray"
                        }
                      >
                        {rule.enabled ? "On" : "Off"}
                      </Pill>
                    </StateRow>
                  ))}
                  {restrictedDays.length > 0 ? (
                    <StateRow name="Restricted days">
                      <Pill className="bg-lightgray text-darkergray">
                        {restrictedDays.join(", ")}
                      </Pill>
                    </StateRow>
                  ) : null}
                </>
              )}
            </Panel>
          </Block>

          <Block title="Links">
            <Panel>
              <DlRow
                label="Privacy policy"
                value={branding.privacyPolicyUrl ? "View" : null}
                href={branding.privacyPolicyUrl}
              />
              <div
                className={`flex items-baseline gap-4 px-4 py-2.5 ${rowBorder}`}
              >
                <span className="text-[13px] text-darkgray flex-shrink-0">
                  Social
                </span>
                <span className="ml-auto min-w-0 text-right">
                  {socialEntries.length === 0 ? (
                    <span className="text-darkgray">Not set</span>
                  ) : (
                    socialEntries.map((key, index) => (
                      <React.Fragment key={key}>
                        {index > 0 ? (
                          <span className="text-darkgray"> · </span>
                        ) : null}
                        <a
                          href={socialLinks[key]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-black capitalize border-b border-mediumgray hover:border-black"
                        >
                          {key}
                        </a>
                      </React.Fragment>
                    ))
                  )}
                </span>
              </div>
            </Panel>
          </Block>
        </div>
      </div>
    </div>
  );
}
